/**
 * Định dạng ngày hiển thị dùng chung cho toàn app — một quy ước duy nhất:
 * ngày `dd-mm-yyyy`, ngày-giờ `dd-mm-yyyy HH:mm`.
 *
 * Hai luật cố ý, khác nhau có chủ đích:
 *
 * 1. NGÀY nghiệp vụ lấy đúng phần ngày trên wire, KHÔNG quy đổi múi giờ. Đơn vị quản lý
 *    ghi ngày nào thì hiển thị đúng ngày đó, không lệch theo máy người xem.
 * 2. NGÀY-GIỜ (nhật ký, lần đồng bộ) hiển thị theo giờ máy người xem, vì đó là thời điểm
 *    thực thi — quy đổi múi giờ mới đúng nghĩa.
 *
 * Giá trị ngoài khuôn giữ nguyên văn: không bao giờ bịa. Thiếu giá trị → gạch dài "—".
 */

/** Khớp phần đầu `yyyy-mm-dd`; đuôi (giờ, múi giờ) bỏ qua — đó là chủ ý của luật 1. */
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * `format` cho AntD `DatePicker` của MỌI ô nhập ngày trong app.
 *
 * Phải là **object** `{ format, type: "mask" }`, không được là chuỗi trần: rc-picker chỉ
 * bật mask khi `format.type === "mask"` (`useFieldFormat`). Có mask thì người dùng gõ
 * `02041972` là tự ra `02-04-1972`; truyền chuỗi `"DD-MM-YYYY"` sẽ hiển thị đúng nhưng
 * **không tự chèn dấu**, và gõ thiếu thì giá trị bị bỏ.
 */
export const DATE_PICKER_MASK_FORMAT: { readonly format: string; readonly type: "mask" } = {
  format: "DD-MM-YYYY",
  type: "mask",
};

function isBlank(value: string | number | null | undefined): value is null | undefined | "" {
  return value === null || value === undefined || value === "";
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** ISO ngày (hoặc ngày-giờ) → `dd-mm-yyyy`; thiếu → "—"; ngoài khuôn → nguyên văn. */
export function formatDateVn(value: string | null | undefined): string {
  if (isBlank(value)) return "—";
  const iso = ISO_DATE_PREFIX.exec(value);
  return iso ? `${iso[3]}-${iso[2]}-${iso[1]}` : value;
}

/**
 * Ngày-giờ → `dd-mm-yyyy HH:mm` (thêm `:ss` khi cần độ chính xác nhật ký).
 * Nhận cả chuỗi ISO lẫn mốc epoch mili-giây (`state.fetchedAt` là số).
 */
export function formatDateTimeVn(
  value: string | number | null | undefined,
  options?: { seconds?: boolean },
): string {
  if (isBlank(value)) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const date = `${pad2(parsed.getDate())}-${pad2(parsed.getMonth() + 1)}-${parsed.getFullYear()}`;
  const time = `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`;
  return options?.seconds === true
    ? `${date} ${time}:${pad2(parsed.getSeconds())}`
    : `${date} ${time}`;
}
